import MobileMode from '@/scripts/mobile'
import createScene, { parkScene, resumeScene } from '@/scripts/canvas/scene'
import { getApp } from '@/scripts/canvas/scene/sceneManager'
import { preloadAssets, ensureActivitiesReady } from '@/scripts/data/assetLoader'
import { closeHiddenPlaceBox } from '@/scripts/canvas/HiddenPlace'
import { dimensions } from '@/scripts/config/settings'
import audioManager from '@/scripts/audio/audioManager'
import { cleanupMobileIframeMenu } from '@/scripts/mobile/modals'
import { cleanupActivityIframe } from '@/scripts/ui/activityModal'

type AppMode = 'mobile' | 'desktop'

let currentMode: AppMode
let resizeTimer = 0
let desktopBooted = false
let assetsLoading = false
let assetsLoaded = false
let enterButtonListenerAdded = false

const enterScreen = document.querySelector<HTMLDivElement>('.enter-screen')

function init(): void {
  // The app must boot in mobile or desktop mode on page load,
  // and then also when going from/to mobile/desktop after load
  currentMode = detectMode()
  boot(currentMode)

  const debouncedModeSwitch = debounce(handleModeSwitch, 300)
  window.addEventListener('resize', debouncedModeSwitch, { passive: true })
  window.addEventListener('orientationchange', debouncedModeSwitch, { passive: true })
}

function detectMode(): AppMode {
  return window.innerWidth >= dimensions.DESKTOP_BREAKPOINT ? 'desktop' : 'mobile'
}

function boot(mode: AppMode) {
  return mode === 'desktop' ? bootDesktop() : bootMobile()
}

function bootDesktop() {
  setCanvasVisible(true)

  // Only set up event listeners once
  if (!desktopBooted) {
    window.addEventListener('keydown', () => document.documentElement.classList.add('using-keyboard'))
    window.addEventListener('mousemove', () => document.documentElement.classList.remove('using-keyboard'))
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') closeHiddenPlaceBox()
    })
    desktopBooted = true
  }

  // Reset enter screen state when switching to desktop (in case it was hidden in mobile)
  if (enterScreen && enterScreen.classList.contains('hidden')) {
    enterScreen.classList.remove('hidden')
    // Reset the button listener flag since we're showing the screen again
    enterButtonListenerAdded = false
  }

  const enterBtn = document.querySelector<HTMLButtonElement>('.enter-button')

  // if (!enterScreen || !enterBtn) {
  //   // No enter screen, start audio immediately (no autoplay restriction)
  //   if (!assetsLoaded && !assetsLoading) {
  //     assetsLoading = true
  //     preloadAssets(() => {
  //       assetsLoading = false
  //       assetsLoaded = true
  //       if (!getApp()) {
  //         createScene()
  //       }
  //     })
  //   } else if (assetsLoaded && !getApp()) {
  //     // Assets loaded but scene not created yet
  //     createScene()
  //   }
  //   audioManager.startAudio()
  //   return
  // }

  // If assets are already loaded, just set up the enter button if needed
  if (assetsLoaded) {
    // If scene already exists, nothing to do
    if (getApp()) {
      return
    }

    // Check if enter screen was already hidden/removed (user already "entered" in mobile)
    const enterScreenHidden = !enterScreen || enterScreen.classList.contains('hidden') || !enterScreen.parentNode

    if (enterScreenHidden) {
      // User already entered, create scene immediately
      if (!getApp()) {
        createScene()
      }
      return
    }

    // Enter screen is visible, ensure button listener is attached
    if (enterBtn && !enterButtonListenerAdded) {
      enterButtonListenerAdded = true
      enterBtn.addEventListener(
        'click',
        () => {
          enterScreen?.remove()
          // Start audio after user interaction to avoid autoplay restrictions
          audioManager.startAudio()
          if (!getApp()) {
            createScene()
          }
        },
        { once: true },
      )
    }
    return
  }

  // Assets not loaded yet, start loading
  if (!assetsLoading) {
    assetsLoading = true
    preloadAssets(() => {
      assetsLoading = false
      assetsLoaded = true

      // After preloader finishes, check enter screen state again
      // (preloader.hide() might have changed the visibility)
      const enterScreenHidden = !enterScreen || enterScreen.classList.contains('hidden') || !enterScreen.parentNode

      if (enterScreenHidden) {
        // User already entered or screen was removed, create scene immediately
        if (!getApp()) {
          createScene()
        }
      } else if (enterBtn) {
        // Enter screen is visible, ensure button listener is attached
        if (!enterButtonListenerAdded) {
          enterButtonListenerAdded = true
          enterBtn.addEventListener(
            'click',
            () => {
              enterScreen?.remove()
              // Start audio after user interaction to avoid autoplay restrictions
              audioManager.startAudio()
              if (!getApp()) {
                createScene()
              }
            },
            { once: true },
          )
        }
      }
    })
  }
}

async function bootMobile() {
  const mobileRoot = document.querySelector<HTMLElement>('.mobile-container')
  const enterBtn = document.querySelector<HTMLButtonElement>('.mobile-enter-button')
  if (!mobileRoot) return

  setCanvasVisible(false)

  if (!MobileMode.isInitialized()) {
    // Load just activities; no Pixi/desktop assets
    await ensureActivitiesReady()
    MobileMode.init(mobileRoot)
    enterBtn?.addEventListener('click', () => {
      // Start audio after user interaction to avoid autoplay restrictions
      audioManager.startAudio()
      enterScreen?.classList.add('hidden')

      // After enter screen is hidden, check if there's a hash and process it
      // Use setTimeout to ensure DOM updates are processed
      setTimeout(() => {
        if (window.location.hash) {
          // Trigger hashchange event to process the hash
          window.dispatchEvent(new Event('hashchange'))
        }
      }, 50)
    })
  } else {
    MobileMode.show()
  }
}

function handleModeSwitch() {
  const next = detectMode()
  if (next === currentMode) return

  // Clean up iframes when switching modes
  cleanupMobileIframeMenu()
  cleanupActivityIframe()

  currentMode = next

  if (currentMode === 'desktop') {
    setCanvasVisible(true)

    const pixiExists = Boolean(getApp())
    if (pixiExists) {
      resumeScene()
    } else {
      bootDesktop()
    }

    MobileMode?.hide()
  } else {
    parkScene()
    setCanvasVisible(false)
    void bootMobile()
  }
}

function setCanvasVisible(visible: boolean) {
  document.querySelector('.canvas-container')?.classList.toggle('hidden', !visible)
}

function debounce(fn: () => void, ms: number) {
  return () => {
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = window.setTimeout(fn, ms)
  }
}

function getCurrentMode(): AppMode {
  return currentMode
}

export default { init, getCurrentMode }
